/**
 * Base API Service
 * 
 * Provides common functionality for API requests
 */
import { API_PATH, API_TIMEOUT, API_INCLUDE_CREDENTIALS, API_KEY } from '../config/api.config';
import axios from 'axios';

// Define public endpoints that don't need tenant context
const PUBLIC_ENDPOINTS = [
  '/health',
  '/api/v1/license/validate',
  '/api/v1/license/activate',
  '/api/v1/license/deactivate',
  '/api/v1/license/info',
  '/api/v1/auth/token',
  '/api/v1/auth/google/login',
  '/api/v1/auth/verify',
  '/api/v1/auth/logout',
  '/api/v1/admin/domains/resolve-public'
];

// Track tenant alias for API calls
let tenantAlias = null;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_PATH,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,  // Set default API key in instance creation
  },
  timeout: API_TIMEOUT || 30000,
  withCredentials: API_INCLUDE_CREDENTIALS,
  maxRedirects: 0,  // Prevent redirects
  validateStatus: function (status) {
    return status >= 200 && status < 300; // Only accept 2xx status codes
  }
});

// Update isPublicPath to only affect auth token, not tenant context
const isPublicPath = (path) => {
  return PUBLIC_ENDPOINTS.some(endpoint => {
    // Exact match
    if (path === endpoint) return true;
    // Path with parameters
    if (path.startsWith(endpoint + '/')) return true;
    return false;
  });
};

// Add request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    const currentTenantAlias = tenantAlias; // This is from the closure scope
    
    // Add debug logging
    console.log('API Request:', {
      url: config.url,
      method: config.method,
      hasToken: !!token,
      tenantAlias: currentTenantAlias,
      isPublicPath: isPublicPath(config.url)
    });
    
    // Always set tenant alias header unless it's an admin route
    if (!config.url.startsWith('/api/v1/admin') && currentTenantAlias) {
      config.headers['X-API-Tenant-Alias'] = currentTenantAlias;
    }
    
    // Add auth token if available and not a public endpoint
    if (token && !isPublicPath(config.url)) {
      console.log('API Request: Adding Authorization header with token');
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Always ensure API key is set
    if (!config.headers['X-API-Key']) {
      config.headers['X-API-Key'] = API_KEY;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Update error handling in response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ERR_NETWORK') {
      console.error('Network Error Details:', {
        url: error.config?.url,
        method: error.config?.method,
        headers: {
          ...error.config?.headers,
          'X-API-Key': 'REDACTED'
        },
        message: error.message
      });
    }
    
    if (error.response?.status === 401) {
      console.error('API Authentication Failed:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.message || 'No error message provided'
      });
      // Clear token and redirect to login
      console.log('API Service: Removing token due to 401 error');
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

// Set global tenant alias for API calls
export const setGlobalTenantAlias = (alias) => {
  if (!alias) {
    console.warn('Attempting to set empty tenant alias');
    return;
  }
  console.log(`Setting global tenant alias for API calls: ${alias}`);
  tenantAlias = alias;
  
  // Update the default headers
  apiClient.defaults.headers.common['X-API-Tenant-Alias'] = alias;
};

// Get current tenant alias
export const getGlobalTenantAlias = () => {
  return tenantAlias;
};

// Clear tenant alias
export const clearGlobalTenantAlias = () => {
  tenantAlias = null;
};

// API service methods
export const apiService = {
  // Standard API methods that include tenant header
  async get(endpoint, config = {}) {
    try {
      const response = await apiClient.get(endpoint, config);
      // If responseType is blob, return the raw response data
      if (config.responseType === 'blob') {
        return response.data;
      }
      return response.data;
    } catch (error) {
      console.error(`GET ${endpoint} failed:`, error);
      throw error;
    }
  },

  async post(endpoint, data = {}, config = {}) {
    try {
      // If data is FormData, don't transform it
      const isFormData = data instanceof FormData;
      const requestConfig = {
        ...config,
        transformRequest: isFormData ? [(data) => data] : undefined
      };

      console.log('Making POST request:', {
        endpoint,
        isFormData,
        headers: requestConfig.headers
      });

      const response = await apiClient.post(endpoint, data, requestConfig);
      return response.data;
    } catch (error) {
      console.error(`POST ${endpoint} failed:`, error);
      throw error;
    }
  },

  async put(endpoint, data = {}) {
    try {
      const response = await apiClient.put(endpoint, data);
      return response.data;
    } catch (error) {
      console.error(`PUT ${endpoint} failed:`, error);
      throw error;
    }
  },

  async patch(endpoint, data = {}) {
    try {
      const response = await apiClient.patch(endpoint, data);
      return response.data;
    } catch (error) {
      console.error(`PATCH ${endpoint} failed:`, error);
      throw error;
    }
  },

  async delete(endpoint) {
    try {
      const response = await apiClient.delete(endpoint);
      return response.data;
    } catch (error) {
      console.error(`DELETE ${endpoint} failed:`, error);
      throw error;
    }
  },

  // Public API methods that don't include tenant header
  async getPublic(endpoint, params = {}) {
    try {
      if (!PUBLIC_ENDPOINTS.some(e => endpoint.includes(e))) {
        console.warn(`Warning: Calling non-public endpoint ${endpoint} with public method`);
      }
      
      // Log API key presence
      console.log('Making public request with API key:', !!API_KEY);
      
      const response = await apiClient.get(endpoint, { 
        params,
        headers: {
          'X-API-Key': API_KEY
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Public GET ${endpoint} failed:`, error);
      console.error('API Key present:', !!API_KEY);
      throw error;
    }
  },

  async postPublic(endpoint, data = {}) {
    try {
      if (!PUBLIC_ENDPOINTS.some(e => endpoint.includes(e))) {
        console.warn(`Warning: Calling non-public endpoint ${endpoint} with public method`);
      }
      const response = await apiClient.post(endpoint, data, {
        headers: {
          'X-API-Key': API_KEY  // Always include API key
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Public POST ${endpoint} failed:`, error);
      throw error;
    }
  }
};

// Export the raw axios instance for advanced use cases
export const axiosInstance = apiClient;

